import { faChevronUp, faMinus, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, IconButton, Text } from '@modusign/design-system';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { FOOTER, GNB } from '@app/constants/dimension';
import {
  Breakpoint,
  ButtonBox,
  getIsMinimized,
  toggleDisplay,
} from '@app/features/formRecommendation';

const CONSTRAINT = {
  CHANNELTALK_OFFSET: 154,
  SHEET: {
    CLOSED_HEIGHT: 82,
    CLOSED_PADDING: 36,
    GUTTER: 24,
    HEADER_HEIGHT: 22,
    MAX_HEIGHT: 316,
    OPENED_PADDING: 48,
  },
  GNB: {
    DESKTOP_WIDTH: GNB.DESKTOP.WIDTH,
    OFFSET: 36,
    MOBILE_HEIGHT: GNB.MOBILE.HEIGHT,
  },
  FOOTER: {
    DESKTOP_HEIGHT: FOOTER.DESKTOP.HEIGHT,
    MOBILE_HEIGHT: FOOTER.MOBILE.HEIGHT,
  },
} as const;

interface MobileFormRecommendationSheetProps {
  breakpoint: Breakpoint;
  formIds: string[];
}

function MobileFormRecommendationSheet({
  breakpoint,
  formIds,
}: MobileFormRecommendationSheetProps) {
  const dispatch = useDispatch();
  const isMinimized = useSelector(getIsMinimized);
  const isMobileViewport = /mobile/.test(breakpoint);
  const buttonBoxRef = useRef<HTMLDivElement>(null);

  useMount(() => {
    if (isMobileViewport) {
      dispatch(toggleDisplay({ isMinimized: true }));
    }
  });

  /**
   * mount되기 전 transition이 정의되면 breakpoint가 바뀔 때 렌더링 이슈가 있습니다.
   * @see https://www.figma.com/file/AfPv3RpxTXfJvuyqalnWdG/230103-AT-%EC%84%9C%EC%8B%9D%EA%B2%80%EC%83%89-%EC%B6%94%EC%B2%9C?node-id=930%3A119996&t=03xFdjxqk0KpwDvg-1
   *  */
  const [isMounted, setIsMounted] = useState(false);
  useMount(() => {
    setIsMounted(true);
  });

  return (
    <Box
      {...(isMobileViewport
        ? {
            bottom: 0,
            left: 0,
            mx: 4,
            width: `calc(100% - ${CONSTRAINT.SHEET.GUTTER}px)`,
          }
        : {
            bottom: CONSTRAINT.FOOTER.DESKTOP_HEIGHT,
            left: CONSTRAINT.GNB.DESKTOP_WIDTH,
            ml: 9,
            mr: 0,
          })}
      p={0}
      position="fixed"
      zIndex={100}
    >
      <Flex
        alignItems="center"
        backgroundColor={isMinimized ? 'pureNavy50' : 'boxBackgroundEmphasisBase'}
        borderColor="boxBorderNeutralBase"
        borderRadius="6px 6px 0 0"
        borderStyle="solid"
        borderWidth="1px 1px 0 1px"
        flexDirection="column"
        height={
          isMinimized
            ? CONSTRAINT.SHEET.CLOSED_HEIGHT + (breakpoint === 'mobile-xs' ? 20 : 0)
            : CONSTRAINT.SHEET.HEADER_HEIGHT +
              CONSTRAINT.SHEET.OPENED_PADDING +
              (buttonBoxRef.current?.offsetHeight ?? 0)
        }
        justifyContent="center"
        maxHeight={CONSTRAINT.SHEET.MAX_HEIGHT}
        width={
          isMobileViewport
            ? '100%'
            : `calc(100vw - ${CONSTRAINT.GNB.DESKTOP_WIDTH}px - ${CONSTRAINT.CHANNELTALK_OFFSET}px)`
        }
        {...(isMinimized
          ? {
              sx: {
                '&:hover': {
                  '& svg': {
                    color: 'iconNeutralDarkest',
                  },
                  '& .title, .description': {
                    color: 'textNeutralDarkest',
                  },
                  '& .badge': {
                    backgroundColor: 'statusBackgroundNeutralDarkest',
                  },
                },
                transition: isMounted ? 'height 0.1s ease-out' : undefined,
              },
            }
          : {
              pb: 8,
              pt: 6,
              px: 6,
              sx: { transition: isMounted ? 'height 0.1s ease-out' : undefined },
            })}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          p={isMinimized ? 6 : undefined}
          position="relative"
          sx={{
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          top={isMinimized ? (buttonBoxRef.current?.offsetHeight ?? 0) / 2 : undefined}
          width="100%"
          onClick={() => dispatch(toggleDisplay({ isMinimized: !isMinimized }))}
        >
          <Flex flexDirection="column">
            <Flex alignItems="center" justifyContent="start">
              {isMinimized && (
                <Flex color="iconNeutralBase" mr="14px">
                  <FontAwesomeIcon height="18px" icon={faChevronUp} width="16px" />
                </Flex>
              )}
              <Flex color="iconNeutralSlightlyDark">
                <FontAwesomeIcon height="11px" icon={faThumbsUp} width="11px" />
              </Flex>
              <Text
                className="title"
                color="textNeutralSlightlyLight"
                fontSize="lg"
                fontWeight="medium"
                mx={1}
              >
                추천 서식
              </Text>
              <Flex
                alignItems="center"
                backgroundColor="statusBackgroundNeutralUltraDark"
                borderRadius="10px"
                className="badge"
                justifyContent="center"
                px={3}
                py="2px"
              >
                <Text color="textNeutralLightest" fontSize="xxs" fontWeight="medium">
                  무료
                </Text>
              </Flex>
              {!isMinimized && (
                <Flex
                  alignItems="center"
                  backgroundColor="statusBackgroundNeutralLightest"
                  borderRadius="10px"
                  justifyContent="center"
                  ml={1}
                  px={3}
                  py="2px"
                >
                  <Text color="textNeutralLight" fontSize="xxs" fontWeight="medium">
                    PC 이용 권장
                  </Text>
                </Flex>
              )}
            </Flex>
            <Text
              className="description"
              color="textNeutralSlightlyLight"
              display={isMinimized ? 'block' : 'none'}
              fontSize="sm"
              ml="26px"
              mt={3}
            >
              전문가가 직접 작성·검토한 서식을 편집하여 저장해 보세요
            </Text>
          </Flex>

          {!isMinimized && (
            <IconButton color="iconNeutralBase">
              <FontAwesomeIcon height="18px" icon={faMinus} width="16px" />
            </IconButton>
          )}
        </Flex>

        <Flex
          flexDirection="column"
          gap={3}
          maxHeight={
            CONSTRAINT.SHEET.MAX_HEIGHT -
            (CONSTRAINT.SHEET.OPENED_PADDING - CONSTRAINT.SHEET.CLOSED_PADDING) -
            CONSTRAINT.SHEET.CLOSED_HEIGHT
          }
          pt={6}
          ref={buttonBoxRef}
          sx={{
            visibility: isMinimized ? 'hidden' : 'visible',
          }}
          width="100%"
        >
          {formIds.map((formId) => (
            <ButtonBox breakpoint={breakpoint} formId={formId} key={formId} />
          ))}
        </Flex>
      </Flex>
    </Box>
  );
}

export default MobileFormRecommendationSheet;
